import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useHistory, useParams, Link } from "react-router-dom";
import Select from "react-select";

import { REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL } from "config";
import { getCategoryFromId, getBrandFromId } from "lib/utils";
import BaseProductMetadataEditor from "pages/ProductCompatibility/components/BaseProductMetadataEditor";

const UpdateProduct = () => {
  const [baseProductMongoId, setBaseProductMongoId] = useState(null);
  const [baseProductLookupLoading, setBaseProductLookupLoading] = useState(
    true
  );
  const [product, setProduct] = useState({
    name: "",
    sku: "",
    type: "physical",
    price: "",
    categories: [],
    brand: null,
    availability: "available",
    weight: "",
    unitCount: 1,
  });
  const [allCategories, setAllCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  let history = useHistory();
  let { id } = useParams();

  const getProduct = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/products/${id}`
      );
      if (response.status === 200) {
        const product = response.data.product;
        if (product.custom_fields[0]?.value) {
          product.unitCount = Number(product.custom_fields[0]?.value);
        }
        delete product.description; // removing description, in order for its value not to be sent to the back-end.
        setProduct(product);
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const getCategories = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/categories`
      );
      if (response.status === 200) {
        const categories = response.data.categories;
        setAllCategories(categories);
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const getBrands = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/brands`
      );
      if (response.status === 200) {
        const brands = response.data.brands;
        setAllBrands(brands);
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const lookupBaseProduct = async (bcId) => {
    setBaseProductLookupLoading(true);
    setBaseProductMongoId(null);
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/base-products/by-bc-id/${bcId}`
      );
      if (response.status === 200 && response.data?.baseProduct?._id) {
        setBaseProductMongoId(response.data.baseProduct._id);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setBaseProductMongoId(null);
      } else {
        console.log("base product lookup: ", error);
        setBaseProductMongoId(null);
      }
    } finally {
      setBaseProductLookupLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
    getBrands();
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }
    getProduct();
    lookupBaseProduct(id);
  }, [id]);

  useEffect(() => {
    if (allCategories.length > 0 && product.categories.length > 0) {
      // Converting array of categories ids into an array of categories objects.
      product.categories = product.categories.map((categoryId) =>
        getCategoryFromId(categoryId, allCategories)
      );
      setProduct(product);
    }
  }, [allCategories, product]);

  useEffect(() => {
    if (allBrands.length > 0 && product.brand_id) {
      // Converting brand id into brand object.
      product.brand = getBrandFromId(product.brand_id, allBrands);
      setProduct(product);
    }
  }, [allBrands, product]);

  const submit = async (values, { resetForm }) => {
    const update = {
      ...values,
    };
    update.categories = update.categories.map((category) => category.id);
    if (update.brand) {
      update.brand_id = update.brand.id;
    }
    try {
      const response = await axios.put(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/products/${id}`,
        update
      );
      if (response.status === 200) {
        resetForm();
        history.push("/admin/products");
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const ProductSchema = Yup.object().shape({
    name: Yup.string().min(1, "Too Short!").required("Required"),
    sku: Yup.string().min(1, "Too Short!").required("Required"),
    type: Yup.string().min(1, "Too Short!").required("Required"),
    price: Yup.number().required("Required"),
    categories: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string(),
          value: Yup.number(),
        })
      )
      .required("Required"),
    brand: Yup.object().shape({
      label: Yup.string(),
      value: Yup.number(),
    }),
    availability: Yup.string().min(1, "Too Short!").required("Required"),
    weight: Yup.number().required("Required"),
    unitCount: Yup.number().required("Required"),
  });

  const formik = useFormik({
    initialValues: product,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: ProductSchema,
    onSubmit: submit,
  });

  const {
    name,
    sku,
    type,
    price,
    categories,
    brand,
    availability,
    weight,
    unitCount,
  } = formik.values;

  return (
    <div>
      <Container>
        <Row>
          <Col xs={{ span: 10, offset: 1 }} md={{ span: 6, offset: 3 }}>
            <div className="page">
              <h1>Update Product</h1>
              {baseProductLookupLoading && (
                <p className="text-muted">Checking base product link…</p>
              )}
              {!baseProductLookupLoading && !baseProductMongoId && (
                <p className="text-muted" style={{ marginBottom: 24 }}>
                  This BigCommerce product is not registered as a <strong>base
                  product</strong> in the warehouse. Marketing categories and
                  hardware spec metadata are only available for base products; add
                  the product in{" "}
                  <Link to="/admin/product-compatibilities">Product
                  compatibilities</Link> first.
                </p>
              )}
              {!baseProductLookupLoading && baseProductMongoId && (
                <div style={{ marginBottom: 32 }}>
                  <h4 className="mb-3">Base product metadata</h4>
                  <p className="text-muted" style={{ fontSize: 14 }}>
                    Mongo marketing categories and spec fields (configurator
                    filters). Edit definitions under{" "}
                    <Link to="/admin/base-product-metadata">Base product
                    metadata</Link>.
                  </p>
                  <BaseProductMetadataEditor
                    baseProductId={baseProductMongoId}
                    onSaved={async () => {
                      await getProduct();
                    }}
                  />
                </div>
              )}
              <Form onSubmit={formik.handleSubmit}>
                <Form.Group controlId="name" size="lg">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={name}
                    onChange={formik.handleChange}
                    isValid={formik.touched.name && !formik.errors.name}
                  />
                </Form.Group>
                <Form.Group controlId="sku" size="lg">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control
                    type="text"
                    name="sku"
                    value={sku}
                    onChange={formik.handleChange}
                    isValid={formik.touched.sku && !formik.errors.sku}
                  />
                </Form.Group>
                <Form.Group controlId="type" size="lg">
                  <Form.Label>Type</Form.Label>
                  <Form.Control
                    as="select"
                    name="type"
                    value={type}
                    onChange={(e) => {
                      formik.setFieldValue("type", e.target.value);
                    }}
                  >
                    <option value="physical">Physical</option>
                    <option value="digital">Digital</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group controlId="price" size="lg">
                  <Form.Label>Price</Form.Label>
                  <Form.Control
                    step="0.01"
                    type="number"
                    name="price"
                    value={price}
                    onChange={formik.handleChange}
                    isValid={formik.touched.price && !formik.errors.price}
                  />
                </Form.Group>
                <Form.Group controlId="categories" size="lg">
                  <Form.Label>Categories</Form.Label>
                  <Select
                    options={allCategories.map((category) => ({
                      value: category.id,
                      label: category.name,
                    }))}
                    isMulti={true}
                    closeMenuOnSelect={true}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "categories",
                        e.map((category) =>
                          getCategoryFromId(category.value, allCategories)
                        )
                      );
                    }}
                    value={categories.map((category) => ({
                      value: category.id,
                      label: category.name,
                    }))}
                    name="categories"
                  />
                </Form.Group>
                <Form.Group controlId="brand" size="lg">
                  <Form.Label>Brand</Form.Label>
                  <Select
                    options={allBrands.map((brand) => ({
                      value: brand.id,
                      label: brand.name,
                    }))}
                    isMulti={false}
                    closeMenuOnSelect={true}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "brand",
                        getBrandFromId(e.value, allBrands)
                      );
                    }}
                    value={
                      product.brand
                        ? { value: product.brand.id, label: product.brand.name }
                        : null
                    }
                    name="brand"
                  />
                </Form.Group>
                <Form.Group controlId="availability" size="lg">
                  <Form.Label>Availability</Form.Label>
                  <Form.Control
                    as="select"
                    name="availability"
                    value={availability}
                    onChange={(e) => {
                      formik.setFieldValue("availability", e.target.value);
                    }}
                  >
                    <option value="available">Available</option>
                    <option value="disabled">Disabled</option>
                    <option value="preorder">Preorder</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group controlId="weight" size="lg">
                  <Form.Label>Weight</Form.Label>
                  <Form.Control
                    step="0.01"
                    type="number"
                    name="weight"
                    value={weight}
                    onChange={formik.handleChange}
                    isValid={formik.touched.weight && !formik.errors.weight}
                  />
                </Form.Group>
                <Form.Group controlId="weight" size="lg">
                  <Form.Label>Unit Count</Form.Label>
                  <Form.Control
                    step="1"
                    type="number"
                    name="unitCount"
                    value={unitCount}
                    onChange={formik.handleChange}
                    isValid={formik.touched.weight && !formik.errors.weight}
                  />
                </Form.Group>
                <Button
                  block
                  size="lg"
                  type="submit"
                  style={{
                    marginBottom: "40px",
                  }}
                  disabled={Object.keys(formik.errors).length !== 0}
                >
                  Update Product
                </Button>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UpdateProduct;
